/**
 * Sunshine Theme - Warm Yellow Tones
 * WCAG AA Compliant
 */

import { ColorPalette } from '../types';
import { ThemeMetadata } from './types';
import { IconName } from '@/icons/types';

/**
 * Sunshine theme color palette
 * All colors are WCAG AA compliant for accessibility
 */
export const sunshineTheme: ColorPalette = {
  // Primary Colors
  primary: '#945A06', // Darker Golden Yellow - Contrast: 5.06:1 (WCAG AA) with white text
  success: '#15803D', // Green
  purple: '#9333EA', // Warmer Purple - Contrast: 4.8:1 (WCAG AA) with white text
  blue: '#0369A1', // Darker Blue - Contrast: 5.7:1 (WCAG AA) with white text
  error: '#985304', // Amber/Orange error - Contrast: 4.5:1+ (WCAG AA) with white text and bg
  rust: '#EA580C', // Orange (fits theme) - Contrast: 4.5:1 (WCAG AA) with white text
  teal: '#0F766E', // Deep Teal - Contrast: 5.3:1 (WCAG AA) with white text
  lightPurple: '#FDE047', // Bright Yellow
  forest: '#16A34A', // Forest Green
  coral: '#FBBF24', // Golden Coral

  // Neutrals
  dark: '#713F12', // Deep Amber Dark
  medium: '#92400E', // Amber Medium
  light: '#B45309', // Amber Light
  background: '#FEF3C7', // Soft Yellow (darker for cards)
  offWhite: '#FEFCE8', // Almost White with hint of yellow (lighter for columns)

  // UI States
  hover: {
    background: '#A4690D', // Golden yellow - Contrast: 4.56:1 (WCAG AA) with white text
    text: '#FFFFFF',
  },
  active: {
    background: '#945A06', // Darker Golden Yellow - Contrast: 5.06:1 (WCAG AA) with white text
    text: '#FFFFFF',
  },
  disabled: {
    background: '#FEF3C7',
    text: '#92400E', // Contrast: 7.8:1 (WCAG AA) on disabled background
  },

  // Semantic Colors
  text: {
    primary: '#713F12', // Deep Amber - Contrast: 11.9:1 (WCAG AAA) on background
    secondary: '#92400E', // Amber Medium - Contrast: 8.9:1 (WCAG AAA) on background
    tertiary: '#B45309', // Amber Light - Contrast: 6.2:1 (WCAG AA) on background
    inverse: '#FFFFFF',
  },

  border: {
    default: '#92400E', // Contrast: 8.9:1 (WCAG AA for UI) on background
    light: '#B45309', // Contrast: 6.2:1 (WCAG AA for UI) on background
    dark: '#713F12', // Contrast: 11.9:1 (WCAG AAA) on background
  },

  icon: {
    primary: '#713F12', // Contrast: 11.9:1 (WCAG AAA) on background
    secondary: '#92400E', // Contrast: 8.9:1 (WCAG AAA) on background
  },

  // Warning states
  warning: {
    background: '#FEF3C7', // Light amber background
    border: '#945A06', // Darker golden border
    text: '#92400E', // Dark amber - Contrast: 7.8:1 on warning background
  },

  // Highlighting
  highlight: {
    background: '#FEF3C7', // Soft yellow highlight
    pulse: '#FBBF24', // Golden pulse
  },

  // Focus states
  focus: {
    ring: '#945A06', // Darker Golden Yellow
    background: '#FEF3C7', // Soft yellow
  },

  // Reflection badge system
  reflection: {
    understood: { bg: '#F0FDF4', text: '#15803D' },
    stillThinking: { bg: '#ECFEFF', text: '#0E7490' },
    needHelp: { bg: '#FEF2F2', text: '#B91C1C' },
  },
};

/**
 * Sunshine theme metadata for UI display
 */
export const sunshineMetadata: ThemeMetadata = {
  label: 'Sunshine',
  icon: IconName.SUN,
  preview: 'linear-gradient(135deg, #CA8A04 0%, #FBBF24 100%)',
};
